import React, { useState } from 'react';
import styled from 'styled-components';
import OvListTableField from '../atoms/OvListTableField';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewListIcon from '@mui/icons-material/ViewList';
import { IconButton, Tooltip } from '@mui/material';
import Colours from '../../../design-system/colours';
import StringUtils from '../../../common/utils/services/string-utils';
import OvConfirmationDialog from '../molecules/OvConfirmationDialog';
import { SurveyResponse } from '../../../common/model/dto/survey/survey-response';
import OvDateParser from '../atoms/OvDateParser';
import OvPrettyUserName from '../atoms/OvPrettyUserName';
import OvViewSurveyResponses from './OvViewSurveyResponses';

interface OvSurveyResponseListRowProps {
  surveyResponse: SurveyResponse;
  deleteResponseCallback?: (id: string) => void;
}

const OvSurveyResponseListRow: React.FC<OvSurveyResponseListRowProps> = ({
  surveyResponse,
  deleteResponseCallback,
}) => {
  const { t } = useTranslation();
  const npsScore = surveyResponse.question_responses?.find(
    (question) => question.represent_nps_score
  )?.value;

  const [
    isOpenResponseDeleteConfirmationDialog,
    setOpenResponseDeleteConfirmationDialog,
  ] = useState<boolean>(false);
  const [isOpenViewSurveyResponseModal, setOpenViewSurveyResponseModal] =
    useState<boolean>(false);

  const onDelete = () => {
    if (surveyResponse.id && deleteResponseCallback) {
      setOpenResponseDeleteConfirmationDialog(false);
      deleteResponseCallback(surveyResponse.id);
    }
  };

  return (
    <>
      <SurveyResponseListTableField>
        <NavLink to={`/users/${surveyResponse.user?.id}`}>
          <OvPrettyUserName user={surveyResponse.user} />
        </NavLink>
      </SurveyResponseListTableField>
      <SurveyResponseListTableField>
        <strong>{npsScore || '-'}</strong>
      </SurveyResponseListTableField>
      <SurveyResponseListTableField>
        <Title>{t(StringUtils.getLabelFromNpsScore(npsScore)) || '-'}</Title>
      </SurveyResponseListTableField>
      <SurveyResponseListTableField>
        <OvDateParser
          date={surveyResponse?.submitted_at}
          format={'MMMM DD, YYYY - hh:mm A'}
        />
      </SurveyResponseListTableField>
      <SurveyResponseListTableField
        style={{ width: '6.25rem', textAlign: 'center' }}
      >
        <StyledIconButton
          onClick={() => setOpenViewSurveyResponseModal(true)}
          className="actionIcon"
        >
          <ViewListIcon />
        </StyledIconButton>
      </SurveyResponseListTableField>
      <SurveyResponseListTableField>
        <Tooltip title={t('surveyResponseList.actions.delete')}>
          <StyledIconButton
            onClick={() => setOpenResponseDeleteConfirmationDialog(true)}
            className="actionIcon"
          >
            <DeleteIcon />
          </StyledIconButton>
        </Tooltip>
      </SurveyResponseListTableField>

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isOpenResponseDeleteConfirmationDialog}
        onCancel={() => setOpenResponseDeleteConfirmationDialog(false)}
        onConfirm={onDelete}
        description="surveyResponseList.actions.permanentResponseDelete"
        title="common.areYouSure"
      />

      <OvViewSurveyResponses
        surveyResponse={surveyResponse}
        isOpen={isOpenViewSurveyResponseModal}
        onCancel={() => setOpenViewSurveyResponseModal(false)}
      />
    </>
  );
};

export default OvSurveyResponseListRow;

const SurveyResponseListTableField = styled(OvListTableField)`
  vertical-align: middle !important;
  padding: 0.75rem;
`;

const Title = styled.h4`
  margin: 0;
`;

const StyledIconButton = styled(IconButton)`
  && {
    color: ${Colours.OV_BASE};
    height: 2rem;
    width: 2rem;

    .MuiSvgIcon-root {
      width: 1.5rem;
      height: 1.5rem;
    }

    &.MuiButtonBase-root {
      padding: 0.375rem;
      border-radius: 50%;
    }
  }
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;
